import { useState } from 'react';
import { Link } from 'react-router';
import { NEWSLETTER_LISTS, type NewsletterList } from '@/types';
import { api } from '@/client/lib/api';
import { track } from '@/client/lib/analytics';
import { Turnstile, TURNSTILE_ENABLED } from '@/components/Turnstile';
import { useI18n } from '@/client/i18n/I18nContext';
import { lp } from '@/client/i18n/locale';

/**
 * The signup band (double opt-in). `eyebrow` / `title` / `body` override the
 * default band copy (the /newsletter page, /shop/hallazgos, a sold piece).
 *
 * Two subscriptions (docs/plan-newsletter-dos-listas.md): `lists` = the ones
 * this block offers (default both), `defaultLists` = the ones ticked at first
 * (default La carta only; Hallazgos is opt-in). With both offered there is one
 * checkbox each and at least one is required; with one, the block's title says
 * which and there is no checkbox for it.
 */
export function Newsletter({
  eyebrow,
  title,
  body,
  lists = NEWSLETTER_LISTS,
  defaultLists = ['letter'],
}: {
  eyebrow?: string;
  title?: string;
  body?: string;
  lists?: readonly NewsletterList[];
  defaultLists?: readonly NewsletterList[];
} = {}) {
  const { t, locale } = useI18n();
  const offered = NEWSLETTER_LISTS.filter((l) => lists.includes(l));
  const single = offered.length === 1;
  const [chosen, setChosen] = useState<NewsletterList[]>(() =>
    single ? offered : offered.filter((l) => defaultLists.includes(l)),
  );
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [company, setCompany] = useState(''); // honeypot
  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error' | 'no_list'>('idle');
  const [tsToken, setTsToken] = useState('');
  const [tsReset, setTsReset] = useState(0);

  // The consent names what is being asked for: one list, or «what I ticked».
  const consentText = single
    ? t(offered[0] === 'finds' ? 'newsletter.consentFinds' : 'newsletter.consentLetter')
    : t('newsletter.consent');

  function toggle(list: NewsletterList, on: boolean) {
    setChosen((cur) => (on ? offered.filter((l) => l === list || cur.includes(l)) : cur.filter((l) => l !== list)));
    if (status === 'no_list') setStatus('idle');
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (chosen.length === 0) {
      setStatus('no_list');
      return;
    }
    if (!consent) {
      setStatus('error');
      return;
    }
    if (TURNSTILE_ENABLED && !tsToken) {
      setStatus('error');
      return;
    }
    setStatus('loading');
    try {
      await api.newsletter({ email, consent, locale, lists: chosen, company, turnstileToken: tsToken });
      track('newsletter_signup');
      setStatus('done');
      setEmail('');
    } catch {
      setStatus('error');
      setTsReset((n) => n + 1);
    }
  }

  return (
    <section className="section container">
      <div className="newsletter">
        <p className="eyebrow" style={{ color: 'var(--color-beige)' }}>{eyebrow ?? t('newsletter.eyebrow')}</p>
        <h2>{title ?? t('newsletter.title')}</h2>
        <p>{body ?? t('newsletter.body')}</p>
        {status === 'done' ? (
          <p style={{ marginTop: 'var(--space-5)', color: 'var(--color-ivory)' }}>
            {t('newsletter.done')}
          </p>
        ) : (
          <form className="newsletter__form" onSubmit={onSubmit} noValidate>
            {single ? null : (
              <fieldset className="newsletter__lists">
                <legend className="visually-hidden">{t('newsletter.listsLegend')}</legend>
                {offered.map((list) => (
                  <label key={list} className="newsletter__list">
                    <input
                      type="checkbox"
                      name="lists"
                      value={list}
                      checked={chosen.includes(list)}
                      onChange={(e) => toggle(list, e.target.checked)}
                    />
                    <span>{t(`newsletter.list.${list}`)}</span>
                  </label>
                ))}
              </fieldset>
            )}
            <label htmlFor="nl-email" className="visually-hidden">
              {t('newsletter.placeholder')}
            </label>
            <input
              id="nl-email"
              type="email"
              required
              placeholder={t('newsletter.placeholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button type="submit" className="btn" disabled={status === 'loading'}>
              {status === 'loading' ? t('newsletter.sending') : t('newsletter.submit')}
            </button>
            <div className="honeypot" aria-hidden>
              <label>
                {t('newsletter.honeypot')}
                <input
                  tabIndex={-1}
                  autoComplete="off"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                />
              </label>
            </div>
            <label
              style={{
                flexBasis: '100%',
                display: 'flex',
                gap: 'var(--space-2)',
                alignItems: 'flex-start',
                color: 'var(--color-beige)',
                fontSize: 'var(--fs-xs)',
              }}
            >
              <input
                type="checkbox"
                style={{ marginTop: 3 }}
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
              />
              <span>
                {consentText}{' '}
                {/* Router Link: the absolute path resolves under the locale basename (/en, /fr). */}
                <Link to={lp('/legal/privacidad')} className="link-underline">
                  →
                </Link>
              </span>
            </label>
            {status === 'error' || status === 'no_list' ? (
              <p role="alert" style={{ flexBasis: '100%', color: 'var(--color-sand)', fontSize: 'var(--fs-sm)' }}>
                {t(status === 'no_list' ? 'newsletter.listsError' : 'newsletter.error')}
              </p>
            ) : null}
          </form>
        )}
      </div>
      {/* Turnstile sits BELOW the dark band, on the white page background — its
          light widget looks out of place inside the black newsletter block. */}
      {TURNSTILE_ENABLED && status !== 'done' ? (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 'var(--space-4)' }}>
          <Turnstile onVerify={setTsToken} resetSignal={tsReset} />
        </div>
      ) : null}
    </section>
  );
}
